<i18n src="./locales.json"></i18n>

<template>
  <v-container
    fluid
    class="fullWidth_whiteBackground mx-auto mt-4 mt-md-6 mb-16 pb-8 fullHeight noTopMargin fullWidth_whiteBackground"
    style="padding: 0 !important"
    aria-label="Login landing page"
  >
    <Alerts context="vault" />
    <v-row>
      <v-spacer></v-spacer>
      <v-col>
        <LocaleSelector />
      </v-col>
    </v-row>
    <v-row v-if="isLoading" class="centerProgress">
      <v-col cols="12">
        <v-progress-circular indeterminate :size="size" :width="width" :color="color" />
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12" v-if="isProcessingUserEvent" class="centerProgress">
        <v-progress-circular indeterminate :size="size" :width="width" :color="color" />
      </v-col>
      <v-col cols="12" class="text-center">
        <img src="@/assets/primerica-logo.svg" height="70" alt="Primerica Beta" />
      </v-col>
      <v-col cols="12" class="text-center pt-2">
        <h1 v-if="isTouchId">{{ $t('L_sign_in_fingerprint_id') }}</h1>
        <h1 v-else-if="isFaceId">{{ $t('L_sign_in_with_face_id') }}</h1>
      </v-col>
      <v-col>
        <v-row class="text-center">
          <v-col cols="12" class="py-4">
            <v-img
              v-if="isTouchId"
              contain
              height="150"
              width="150"
              alt="Primerica"
              class="mx-auto mb-2"
              src="../assets/touchId.png"
            />
            <v-img
              v-else-if="isFaceId"
              contain
              height="150"
              width="150"
              alt="Primerica"
              class="mx-auto mb-2"
              src="../assets/faceId.png"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" class="justify-center mt-4 pb-12 px-10 mb-16">
        <v-row class="text-center mb-12 d-flex justify-center">
          <v-btn
            large
            color="primary"
            @click="biometricsLogin"
            :class="[{ 'btn-text-xs-fr': $vuetify.display.xs && isFrenchLocale }]"
            class="maxWidthButton"
          >
            {{ isTouchId ? $t('L_use_fingerprint_id') : $t('L_use_face_id') }}
          </v-btn>

          <v-btn
            large
            block
            color="#02579e"
            variant="text"
            @click="goToOktaSignIn"
            :aria:label="$t('L_ill_use_my_password')"
            class="py-4 my-4"
          >
            {{ $t('L_ill_use_my_password') }}
          </v-btn>
        </v-row>
      </v-col>
      <v-col cols="12">
        <Footer
          :is-mobile="'false'"
          :show-domestic-violence-card="showDomesticViolenceCard"
          :current-locale="currentLocale"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import Alerts from '@/components/AlertsComponent.vue';
import { COUNTRIES } from '@/shared/constants';
import {
  hasFaceId,
  hasTouchId,
  canUseBiometrics,
  hasStrongBiometricsStrengthLevel,
  canUnlock,
  unlockVaultWithBiometrics,
} from '@/plugins/auth/utils/sessionManager';
import Footer from '../../../components/Footer/Footer.vue';
import { useGlobalStore } from '@/stores/global/global.store';
import { mapActions, mapState, mapStores } from 'pinia';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import { type ComponentOptions, defineComponent } from 'vue';
import LocaleSelector from '@/components/LocaleSelector/LocaleSelector.vue';

const componentOptions: ComponentOptions = {
  name: 'LoginLandingPage',
  components: {
    Footer,
    Alerts,
    LocaleSelector,
  },
  props: {
    color: {
      type: String,
      default: '#063562',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    size: {
      type: Number,
      default: 70,
    },
    width: {
      type: Number,
      default: 7,
    },
  },
  data: () => ({
    isLoading: true,
    isProcessingUserEvent: false,
    isFaceId: false,
    isTouchId: false,
    canUseFaceId: false,
    canUseTouchId: false,
    biometricsStrengthStrong: false,
    canUseBiometricsAndNoPreferenceYet: false,
    languagesPrefs: window.appConfig.languagePreferences,
    languages: [],
    useBiometrics: false,
  }),
  computed: {
    ...mapStores(useGlobalStore),
    ...mapState(useCustomerStore, ['customerProfile']),
    ...mapState(useGlobalStore, {
      currentLocale: 'getLocale',
    }),
    // ...mapGetters({
    //   currentLocale: 'getLocale',
    //   isIos: 'isIos',
    //   isAndroid: 'isAndroid',
    // }),
    languageFlag() {
      if (this.globalStore.containsFeature('LANGUAGE-TOGGLE')) {
        if (this.languages?.length > 1) {
          return true;
        }
      }
      return false;
    },
    isMobileApp() {
      return CapacitorFacade.isIos() || CapacitorFacade.isAndroid();
    },
    showDomesticViolenceCard() {
      return this.customerProfile?.country !== COUNTRIES.CANADA;
    },
    isFrenchLocale() {
      return this.currentLocale?.toLowerCase().includes('fr');
    },
    locales() {
      return [
        { title: 'English (US)', value: 'en-US' },
        { title: 'Español (US)', value: 'es-US' },
        { title: 'English (CAN)', value: 'en-CA' },
        { title: 'French (CAN)', value: 'fr-CA' },
      ];
    },
  },
  methods: {
    ...mapActions(useGlobalStore, ['changeLocale', 'goToLogin']),
    async biometricsLogin() {
      if (!this.isProcessingUserEvent) {
        this.isProcessingUserEvent = true;
        window.setTimeout(() => {
          this.isProcessingUserEvent = false;
        }, 3000);
        await unlockVaultWithBiometrics()
          .then(() => {
            this.$router.push({ name: 'Dashboard' });
          })
          .catch(async (err) => {
            try {
              this.$dynatrace.reportCustomError(
                'PC2_MOBILE_ERR',
                `[LoginLandingPage] ${err?.code}: ${err?.message || JSON.stringify(err)}`
              );
              await this.globalStore.postAuditLog({
                actionId: 'MobileApp_Device_FailureAction', // optional
                details: `[${Date.now()}] ERROR CODE ${err?.code}`, // Additional message stored - optional
                message: err?.message, // Error type - optional
              });
            } catch (_) {
              /*  */
            }
          });
        this.isProcessingUserEvent = false;
      }
    },
    async goToOktaSignIn() {
      if (!this.isProcessingUserEvent) {
        this.isProcessingUserEvent = true;
        window.setTimeout(() => {
          this.isProcessingUserEvent = false;
        }, 3000);
        await this.goToLogin().catch((err: unknown) => {
          try {
            this.$dynatrace.reportCustomError(
              'PC2_MOBILE_ERR',
              `[LoginLandingPage - Login failed]: ${JSON.stringify(err)}`
            );
          } catch (_) {
            /*  */
          }
        });
        this.$router.push({ name: 'Dashboard' });
        this.isProcessingUserEvent = false;
      }
    },
  },
  async mounted() {
    // Check to show for biometrics
    const isNativePlatform = CapacitorFacade.isNativePlatform();
    if (isNativePlatform) {
      await Promise.all([
        hasFaceId(),
        hasTouchId(),
        canUseBiometrics(),
        hasStrongBiometricsStrengthLevel(),
        canUnlock(),
      ]).then(
        ([
          _hasFaceId,
          _hasTouchId,
          _canUseBiometrics,
          _hasStrongBiometricsStrengthLevel,
          _canUnlock,
        ]) => {
          this.isFaceId = _hasFaceId;
          this.isTouchId = _hasTouchId;
          this.canUseBiometricsAndNoPreferenceYet =
            _canUnlock && _canUseBiometrics && _hasStrongBiometricsStrengthLevel;
          window.setTimeout(() => {
            this.isLoading = false;
          }, 2800);
        }
      );
    }
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.btnWidth {
  max-width: 300px !important;
  width: 100%;
  margin: auto;
}
.fullHeight {
  min-height: 100vh !important;
}

.noTopMargin {
  margin-top: -15px !important;
}
.link {
  font-weight: 600;
  color: #02579e;
  cursor: pointer;
}

.fullWidth_whiteBackground {
  background-color: #ffffff !important;
  max-width: 100% !important;
}
.centerProgress {
  left: calc(50% + -38px);
  top: 50vh;
  position: absolute;
}
.btn-text-xs-fr :deep(.v-btn__content) {
  font-size: 0.7rem;
  white-space: normal;
  line-height: 1.2;
}
.maxWidthButton {
  max-width: 420px !important;
  width: 100% !important;
  margin: auto;
}
</style>
